/**
 * WebAuthn Wrapper Module
 *
 * This module provides low-level wrappers around the native WebAuthn API
 * (navigator.credentials.create and navigator.credentials.get) with support
 * for custom extensions like PRF and LargeBlob.
 *
 * These functions preserve the exact WebAuthn logic from the working implementation
 * while adding TypeScript types, error handling, and proper abstraction.
 *
 * @module core/webauthn
 */

import { encodeCustomOptionsToSalt, type CustomOptions } from './encoder';
import { bufferToBase64, base64ToBuffer } from '../utils/buffer';

/**
 * Options for creating a new WebAuthn credential (registration).
 */
export interface CreateCredentialOptions {
  /**
   * The username for the credential.
   * This will be displayed during authentication.
   */
  username: string;

  /**
   * The relying party domain (e.g., 'example.com').
   * Must match the current origin's domain.
   */
  domain: string;

  /**
   * Optional cryptographic challenge for registration.
   * If not provided, a random 32-byte challenge will be generated.
   */
  challenge?: Uint8Array;

  /**
   * Optional user ID for the credential.
   * If not provided, a random 16-byte user ID will be generated.
   */
  userId?: Uint8Array;

  /**
   * Optional display name for the user.
   * If not provided, defaults to the username.
   */
  displayName?: string;

  /**
   * Optional relying party name.
   * Defaults to 'WebAuthn Demo' if not provided.
   */
  rpName?: string;

  /**
   * Optional timeout in milliseconds.
   * Defaults to 60000ms (60 seconds) if not provided.
   */
  timeout?: number;

  /**
   * Optional authenticator attachment constraint.
   * - 'cross-platform': Roaming/external authenticator (USB HID, NFC, BLE) — use for physical UltraPass device on desktop
   * - 'platform': Built-in authenticator (Face ID, Touch ID, Windows Hello, Android biometrics)
   * - undefined: No constraint — browser offers all available authenticators (default)
   */
  authenticatorAttachment?: AuthenticatorAttachment;

  /**
   * LargeBlob support level to request at registration.
   *
   * The credential must declare largeBlob support at creation for the biometric-proof JWE
   * to be readable during authentication. Defaults to 'required', matching native.
   *
   * @default 'required'
   */
  largeBlobSupport?: 'required' | 'preferred';

  /**
   * Which extension name declares blob support at registration.
   *
   * 'largeBlobKey' matches the working reference implementation; 'largeBlob' matches the
   * WebAuthn Level 3 spec and native. Defaults to 'largeBlobKey'.
   */
  largeBlobExtension?: 'largeBlob' | 'largeBlobKey';

  /**
   * Whether to request the PRF (hmac-secret) extension.
   *
   * The PF adapter's enrollment template requests no PRF at all. Resolved from
   * `SDKConfig.prfExtension` by the SDK, which knows the platform.
   *
   * @default true
   */
  requestPrf?: boolean;

  /**
   * Whether to send the CTAP2.1 `credProtect` extension keys.
   *
   * Resolved from `SDKConfig.credProtectExtension` by the SDK.
   *
   * @default true
   */
  requestCredProtect?: boolean;

  /**
   * Whether to enforce {@link timeout} with an AbortController.
   *
   * Resolved from `SDKConfig.enforceTimeoutWithAbort` by the SDK.
   *
   * @default false
   */
  enforceTimeoutWithAbort?: boolean;
}

/**
 * Result data from successful credential creation.
 */
export interface Credential {
  /**
   * Base64-encoded credential ID (rawId).
   * This uniquely identifies the credential.
   */
  credentialId: string;

  /**
   * Base64-encoded attestation object.
   * Contains authenticator data and attestation statement.
   */
  attestationObject: string;

  /**
   * Base64-encoded client data JSON.
   * Contains challenge, origin, and type information.
   */
  clientDataJSON: string;

  /**
   * Whether the authenticator confirmed `largeBlob` support for this credential.
   *
   * True only when the create response echoed `largeBlob: { supported: true }`. False means the
   * credential can never carry the biometric-proof JWE, so any later verification will fail —
   * check this before treating a registration as usable, rather than discovering it at the next
   * assertion.
   */
  largeBlobSupported: boolean;

  /**
   * Raw WebAuthn credential object.
   * Useful for accessing extension results.
   */
  rawCredential: PublicKeyCredential;
}

/**
 * Options for getting an existing WebAuthn credential (authentication).
 */
export interface GetCredentialOptions {
  /**
   * The relying party domain (e.g., 'example.com').
   * Must match the current origin's domain.
   */
  domain: string;

  /**
   * Optional cryptographic challenge for authentication.
   * If not provided, a random 32-byte challenge will be generated.
   */
  challenge?: Uint8Array;

  /**
   * Optional array of allowed credential IDs (base64-encoded).
   * If provided, only these credentials can be used for authentication.
   */
  allowedCredentials?: string[];

  /**
   * Optional custom authentication options.
   * These will be encoded into a salt and passed via the PRF extension.
   */
  customOptions?: CustomOptions;

  /**
   * Optional timeout in milliseconds.
   * Defaults to 60000ms (60 seconds) if not provided.
   */
  timeout?: number;

  /**
   * Optional AbortSignal to cancel the authentication request.
   * Can be used to implement a cancel button or timeout logic.
   */
  signal?: AbortSignal;

  /**
   * Whether to request the PRF extension when {@link customOptions} are present.
   *
   * The PF adapter's assertion template requests only `largeBlob: { read: true }`. Resolved from
   * `SDKConfig.prfExtension` by the SDK, which knows the platform.
   *
   * @default true
   */
  requestPrf?: boolean;

  /**
   * Transports hint applied to each `allowCredentials` entry, or `null` to omit it.
   *
   * A hint that omits the authenticator's real transport can make a valid credential
   * unmatchable, surfacing as `NotAllowedError`. Resolved from
   * `SDKConfig.allowCredentialsTransports` by the SDK.
   *
   * @default ['internal', 'hybrid', 'usb']
   */
  transports?: AuthenticatorTransport[] | null;

  /**
   * Whether to enforce {@link timeout} with an AbortController.
   *
   * Ignored when an explicit {@link signal} is supplied. Resolved from
   * `SDKConfig.enforceTimeoutWithAbort` by the SDK.
   *
   * @default false
   */
  enforceTimeoutWithAbort?: boolean;

  /**
   * Fallback used when the assertion returns no `largeBlob`.
   *
   * Receives the base64 `clientDataJSON` of the completed assertion and resolves with the payload
   * text (same shape `largeBlob` would have delivered) or null. Injected rather than called
   * directly so this module stays free of transport concerns and testable without a socket; the
   * SDK supplies `core/loopback.fetchAssertionPayload` when configured.
   *
   * Only consulted when the blob is genuinely absent, so a transport that does deliver one is
   * unaffected.
   */
  fetchAssertionPayload?: (clientDataJSON: string) => Promise<string | null>;
}

/**
 * Result data from successful credential authentication.
 */
export interface AuthenticationResult {
  /**
   * Base64-encoded credential ID that was used.
   */
  credentialId: string;

  /**
   * Base64-encoded authenticator data.
   * Contains RP ID hash, flags, signature counter, and extensions.
   */
  authenticatorData: string;

  /**
   * Base64-encoded client data JSON.
   */
  clientDataJSON: string;

  /**
   * Base64-encoded signature over authenticator data and client data hash.
   */
  signature: string;

  /**
   * Base64-encoded user handle (user ID), or null if not available.
   */
  userHandle: string | null;

  /**
   * The JWE token itself, or null if no biometric proof was available.
   *
   * Use this for display, logging, or handing to your own backend. To verify against the
   * PrivateID endpoint use {@link verifyPayload} instead — the endpoint expects the whole
   * payload object, not just this token.
   */
  jweToken: string | null;

  /**
   * Exact request body to POST to the verify endpoint, or null if no proof was available.
   *
   * For the current JSON blob format this is the blob with `encrypted_embedding` removed —
   * i.e. `{"jwe_token":"...","device_uuid":"...","version":"..."}` — sent as the raw body.
   * For the legacy plain-token format it is the bare token, which the API client wraps as
   * `{ jwe_token }`.
   *
   * Kept separate from {@link jweToken} because the two are not interchangeable: posting
   * the token alone drops `device_uuid`/`version`, and posting this object inside a
   * `jwe_token` field double-wraps it.
   */
  verifyPayload: string | null;

  /**
   * Base64 face embedding encrypted with ECIES, or null when not present.
   *
   * Only populated when the credential was registered with a `verifyPublicKey`. Used for
   * local silent re-verification; stripped from {@link verifyPayload} before sending.
   */
  encryptedEmbedding: string | null;

  /**
   * PRF extension results, or null if not available.
   * Contains the pseudo-random function output.
   */
  prfResults: {
    first: string; // Base64-encoded
    second?: string; // Base64-encoded (optional)
  } | null;

  /**
   * Raw WebAuthn assertion object.
   * Useful for accessing additional extension results.
   */
  rawAssertion: PublicKeyCredential;
}

/**
 * Splits a largeBlob payload into the pieces callers need.
 *
 * Two formats exist in the wild and both must keep working:
 *
 * 1. **Current (JSON)** — `{"jwe_token":"...","device_uuid":"...","version":"...",
 *    "encrypted_embedding":"..."}`. The embedding is extracted separately and stripped; the
 *    remaining object is the raw verify request body. Sending only `jwe_token` here would
 *    drop `device_uuid` and `version`, which the endpoint expects.
 * 2. **Legacy (plain string)** — a bare JWE token, which the API client wraps as
 *    `{ jwe_token }`.
 *
 * Mirrors the native handling in the native iOS launcher (and
 * `verifyJWEPayload`, which posts the stripped object as the raw HTTP body).
 *
 * @param decoded - UTF-8 decoded largeBlob contents
 * @returns The token, the exact verify request body, and the encrypted embedding if present
 */
export function parseLargeBlobPayload(decoded: string): {
  jweToken: string | null;
  verifyPayload: string | null;
  encryptedEmbedding: string | null;
} {
  let parsed: Record<string, unknown> | null = null;

  try {
    const candidate = JSON.parse(decoded);
    // A JWE is a dot-delimited string, so a bare token parses as a JSON *string*, not an
    // object. Only treat real objects as the structured format.
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      parsed = candidate as Record<string, unknown>;
    }
  } catch {
    // Not JSON — legacy plain-token format.
  }

  if (!parsed) {
    const token = decoded.trim();
    if (token.length === 0) {
      return { jweToken: null, verifyPayload: null, encryptedEmbedding: null };
    }
    console.log('largeBlob: legacy plain-token format');
    return { jweToken: token, verifyPayload: token, encryptedEmbedding: null };
  }

  const embedding = parsed.encrypted_embedding;
  const encryptedEmbedding = typeof embedding === 'string' ? embedding : null;

  // Strip the embedding — it is for local use and must not go to the verify endpoint.
  const payloadForVerify: Record<string, unknown> = { ...parsed };
  delete payloadForVerify.encrypted_embedding;

  const token = parsed.jwe_token;

  console.log(
    `largeBlob: JSON format (encrypted_embedding: ${encryptedEmbedding ? 'present' : 'absent'})`
  );

  return {
    jweToken: typeof token === 'string' ? token : null,
    verifyPayload: JSON.stringify(payloadForVerify),
    encryptedEmbedding
  };
}

/**
 * Custom error class for WebAuthn-related errors.
 */
export class WebAuthnError extends Error {
  /**
   * Error code identifying the type of error.
   */
  public code: WebAuthnErrorCode;

  /**
   * Additional error details.
   */
  public details?: unknown;

  constructor(code: WebAuthnErrorCode, message: string, details?: unknown) {
    super(message);
    this.name = 'WebAuthnError';
    this.code = code;
    this.details = details;
  }
}

/**
 * Error codes for WebAuthn operations.
 */
export enum WebAuthnErrorCode {
  /** WebAuthn API is not supported in this browser */
  NOT_SUPPORTED = 'NOT_SUPPORTED',
  /** User cancelled the operation */
  USER_CANCELLED = 'USER_CANCELLED',
  /** Operation timed out */
  TIMEOUT = 'TIMEOUT',
  /** Invalid parameters provided */
  INVALID_PARAMS = 'INVALID_PARAMS',
  /** Credential creation failed */
  CREATION_FAILED = 'CREATION_FAILED',
  /** Credential retrieval failed */
  RETRIEVAL_FAILED = 'RETRIEVAL_FAILED',
  /** Unknown error occurred */
  UNKNOWN = 'UNKNOWN',
}

/**
 * Converts a thrown value into a plain, JSON-serializable descriptor.
 *
 * `DOMException` (and `Error`) carry `name`/`message` on the prototype as non-enumerable
 * properties, so `JSON.stringify(domException)` yields `"{}"` — any log or telemetry pipeline
 * that stringifies the error silently discards the only fields that identify it. A Windows
 * registration failure then reads as `{"code":"CREATION_FAILED","details":{"originalError":{}}}`,
 * which says nothing about whether the authenticator reported NotSupportedError, ConstraintError
 * or UnknownError — three different bugs with three different fixes.
 *
 * Copying the fields onto an own-property object makes them survive serialization.
 *
 * @param error - The caught value
 * @returns A plain object safe to stringify
 */
export function describeError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      // DOMException.code is the legacy numeric code; harmless when absent.
      ...(typeof (error as DOMException).code === 'number' && { domCode: (error as DOMException).code }),
      ...(error.stack && { stack: error.stack }),
    };
  }

  if (error && typeof error === 'object') {
    return { name: 'UnknownObject', message: String(error), raw: error };
  }

  return { name: typeof error, message: String(error) };
}

/**
 * Largest RP ID the UltraPass authenticator can store per resident key, in bytes.
 *
 * The authenticator stores each resident key's RP ID in a fixed 48-byte field and the write
 * clamps to it. The clamp is memory-safe but silent: nothing records that the value was cut,
 * so an over-long RP ID fails much later and nowhere near the cause.
 */
export const RK_STORE_RP_ID_MAX_BYTES = 48;

/**
 * Warns when an RP ID is too long for the authenticator's resident-key store.
 *
 * Worth a dedicated check because the consequences are severe and land nowhere near the cause.
 * The C library matches credentials by `rpIdHash` (SHA-256 of the *full* RP ID), so registration
 * and the assertion both appear to work. The iOS credential-provider extension, though, compares
 * and looks up by the RP ID *string* it reads back from the resident key — now 48 bytes — against
 * the full value from the WebAuthn request. Observed on 2026-08-04 with a 51-byte RP ID:
 *
 *   rpId mismatch: stored=<51 bytes>, expected=<48 bytes>
 *   Verification not valid for selected credential — trying Face ID fallback
 *   Got 0 credential(s) from authenticator for RP: <48 bytes>
 *   Credential no longer exists in FIDO2 authenticator — cleaning up
 *   → deletes the credential AND its face enrollment
 *
 * So a too-long RP ID does not merely fail: it makes the authenticator destroy the enrollment it
 * just created, which then reads as `credential_invalid` on the next attempt and leaves an
 * embedding-less resident key behind. Naming it up front is far cheaper than tracing that chain.
 *
 * @param domain - The RP ID about to be used
 */
function warnIfRpIdTooLongForStore(domain: string): void {
  // Byte length, not character count — the C buffer is bytes, and an IDN hostname can exceed 48
  // bytes while looking shorter.
  const byteLength = new TextEncoder().encode(domain).length;

  if (byteLength > RK_STORE_RP_ID_MAX_BYTES) {
    console.warn(
      `[FIDO2-JS] RP ID is ${byteLength} bytes, over the ${RK_STORE_RP_ID_MAX_BYTES}-byte limit ` +
        `of the authenticator's resident-key store ("${domain}"). It will be stored truncated, ` +
        'and on iOS the credential provider then compares the truncated value against the full ' +
        'one, concludes the credential no longer exists, and DELETES the credential together ' +
        'with its face enrollment — surfacing later as reason=credential_invalid. Use a hostname ' +
        `of at most ${RK_STORE_RP_ID_MAX_BYTES} bytes.`
    );
  }
}

/**
 * Runs a credentials call, optionally enforcing its timeout with an AbortController.
 *
 * `publicKey.timeout` is only a hint the platform may ignore. On Windows the native WebAuthn
 * dialog can stay open well past it while the authenticator keeps retrying the face match, so
 * the ceremony never settles and the caller's `timeout` means nothing. `abort()` sends the CTAP
 * cancel, dismisses the OS dialog, and rejects — which is at least a definite outcome.
 *
 * Mirrors the PingFederate adapter's approach in
 * `ultrapass-pf-adapter/templates/ultrapass.webauthn.template.html`.
 *
 * @param invoke - Receives the abort signal (undefined when enforcement is off)
 * @param timeoutMs - Timeout to enforce
 * @param enforce - Whether to arm the controller at all
 * @param label - Included in the log line on expiry
 */
async function callWithEnforcedTimeout<T>(
  invoke: (signal?: AbortSignal) => Promise<T>,
  timeoutMs: number,
  enforce: boolean,
  label: string
): Promise<T> {
  if (!enforce || typeof AbortController === 'undefined') {
    return invoke(undefined);
  }

  const controller = new AbortController();
  const timer = setTimeout(() => {
    console.warn(
      `[FIDO2-JS] ${label}: timeout of ${timeoutMs}ms reached — aborting. The platform dialog ` +
        'ignored publicKey.timeout.'
    );
    try {
      controller.abort(new DOMException(`${label} timed out`, 'AbortError'));
    } catch {
      // Older browsers reject abort(reason); the signal still fires.
      controller.abort();
    }
  }, timeoutMs);

  try {
    return await invoke(controller.signal);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Checks if WebAuthn is supported in the current browser.
 *
 * @returns true if WebAuthn is supported, false otherwise
 */
export function isWebAuthnSupported(): boolean {
  // More lenient check that works on Android browsers
  // Some Android browsers have PublicKeyCredential as an object/interface
  // rather than a constructor function
  if (typeof window === 'undefined' || !window.PublicKeyCredential) {
    return false;
  }

  // Additionally verify that navigator.credentials API is available
  // This is the actual API we use for create/get operations
  if (typeof navigator === 'undefined' || !navigator.credentials) {
    return false;
  }

  // Check if the essential methods exist
  if (typeof navigator.credentials.create !== 'function' ||
      typeof navigator.credentials.get !== 'function') {
    return false;
  }

  return true;
}

/**
 * Creates a new WebAuthn credential (registration).
 *
 * This function wraps the native `navigator.credentials.create()` API with:
 * - Automatic challenge and user ID generation
 * - PRF and LargeBlob extension support
 * - Type-safe options and results
 * - Comprehensive error handling
 *
 * The created credential can be stored on the server and used for future
 * authentication operations.
 *
 * @param options - Options for credential creation
 * @returns A promise that resolves to the created credential data
 * @throws {WebAuthnError} If credential creation fails
 *
 * @example
 * ```typescript
 * try {
 *   const credential = await createCredential({
 *     username: 'john.doe',
 *     domain: 'example.com',
 *     displayName: 'John Doe'
 *   });
 *
 *   console.log('Credential ID:', credential.credentialId);
 *   // Send credential to server for storage
 * } catch (error) {
 *   if (error instanceof WebAuthnError) {
 *     console.error('WebAuthn error:', error.code, error.message);
 *   }
 * }
 * ```
 *
 * @remarks
 * - Challenge: A random 32-byte value is generated if not provided
 * - User ID: A random 16-byte value is generated if not provided
 * - Extensions: PRF and LargeBlob (write support required) are enabled
 * - Attestation: Set to 'none' for privacy (no authenticator attestation)
 * - Authenticator Selection: Prefers user verification and resident keys
 */
export async function createCredential(
  options: CreateCredentialOptions
): Promise<Credential> {
  // Validate WebAuthn support
  if (!isWebAuthnSupported()) {
    throw new WebAuthnError(
      WebAuthnErrorCode.NOT_SUPPORTED,
      'WebAuthn is not supported in this browser'
    );
  }

  // Validate required parameters
  if (!options.username || typeof options.username !== 'string') {
    throw new WebAuthnError(
      WebAuthnErrorCode.INVALID_PARAMS,
      'username is required and must be a string'
    );
  }

  if (!options.domain || typeof options.domain !== 'string') {
    throw new WebAuthnError(
      WebAuthnErrorCode.INVALID_PARAMS,
      'domain is required and must be a string'
    );
  }

  warnIfRpIdTooLongForStore(options.domain);

  try {
    // Generate challenge if not provided
    const challenge = options.challenge || crypto.getRandomValues(new Uint8Array(32));

    // Generate user ID if not provided
    // Convert to UTF-8 compatible format using base64 (same as original implementation)
    let userId: Uint8Array;
    if (options.userId) {
      userId = options.userId;
    } else {
      const randomBytes = crypto.getRandomValues(new Uint8Array(16));
      const userIdString = bufferToBase64(randomBytes.buffer);
      userId = new TextEncoder().encode(userIdString);
    }

    // Build PublicKeyCredentialCreationOptions (same structure as original)
    const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
      challenge: challenge as BufferSource,
      rp: {
        name: options.rpName || 'WebAuthn Demo',
        id: options.domain,
      },
      user: {
        id: userId as BufferSource,
        name: options.username,
        displayName: options.displayName || options.username,
      },
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' }, // ES256
        { alg: -257, type: 'public-key' }, // RS256
      ],
      authenticatorSelection: {
        ...(options.authenticatorAttachment && { authenticatorAttachment: options.authenticatorAttachment }),
        // Native registers through ASAuthorizationPlatformPublicKeyCredentialProvider, whose
        // passkeys are ALWAYS discoverable and always user-verified — ASAuthorization exposes no
        // residentKey knob because being resident is not optional there. 'required' is therefore
        // the faithful browser equivalent of what the launcher produces.
        //
        // This was 'preferred', which let the authenticator create a NON-discoverable credential.
        // A Phase 2 assertion that relies on resident-key discovery then has nothing to find and
        // the browser rejects with NotAllowedError — indistinguishable from the user cancelling.
        userVerification: 'required',
        requireResidentKey: true,
        residentKey: 'required',
      },
      timeout: options.timeout || 60000,
      attestation: 'direct',
      excludeCredentials: [],
      extensions: {
        // Each of the three blocks below is a divergence from the PF adapter's enrollment
        // template, which sends `extensions: { largeBlob: { support: 'preferred' } }` and
        // nothing else. They are individually switchable so a Windows failure can be bisected
        // instead of guessed at. Defaults stay on to preserve the deeplink flows.
        // credProtect: use CTAP2.1 flat format (top-level keys) rather than the nested
        // WebAuthn Level 3 format ({ credProtect: { credentialProtectionPolicy, ... } }).
        // WebAuthn relaxed its requirement to not break enterprise CTAP2.1 deployments.
        // See: https://github.com/w3c/webauthn/issues/1430
        //
        // The authenticator receives the policy as a CBOR integer ("credProtect": N):
        //   1 = userVerificationOptional          — no UV required (discoverable or not)
        //   2 = userVerificationOptionalWithCredentialIDList — UV optional if allowList provided; required for discoverable
        //   3 = userVerificationRequired           — UV always required
        //
        // Level 1 allows discoverable (passkey-style) auth without requiring CTAP2 UV.
        // UltraPass performs biometric verification at the application layer via the
        // PrivateID backend — not as a CTAP2 UV assertion.
        ...(options.requestCredProtect !== false && {
          credentialProtectionPolicy: 'userVerificationOptional',
          enforceCredentialProtectionPolicy: false,
        }),
        ...(options.requestPrf !== false && { prf: {} }),
        // `largeBlob` is the WebAuthn Level 3 extension that carries the biometric-proof JWE,
        // and it is what the current launcher requests:
        //   the native iOS launcher registers largeBlob as required
        // paired with `assertionRequest.largeBlob = .read` on the assertion.
        //
        // `largeBlobKey` is a different, CTAP2-level extension for fetching a per-credential
        // blob *key*; browsers do not map one onto the other. Older web references
        // (webauthn_client/webauthn_v2.js, poc-deeplink) are no longer in use — do not take
        // either as the contract.
        ...(options.largeBlobExtension === 'largeBlobKey'
          ? { largeBlobKey: { support: options.largeBlobSupport || 'required' } }
          : { largeBlob: { support: options.largeBlobSupport || 'required' } }),
      } as AuthenticationExtensionsClientInputs,
    };

    console.log('Registration options:', publicKeyCredentialCreationOptions);

    // Create the credential
    const credential = (await callWithEnforcedTimeout(
      (signal) =>
        navigator.credentials.create({
          publicKey: publicKeyCredentialCreationOptions,
          ...(signal && { signal }),
        }),
      options.timeout || 60000,
      options.enforceTimeoutWithAbort === true,
      'registration'
    )) as PublicKeyCredential | null;

    if (!credential) {
      throw new WebAuthnError(
        WebAuthnErrorCode.CREATION_FAILED,
        'Credential creation returned null'
      );
    }

    // Extract response data
    const response = credential.response as AuthenticatorAttestationResponse;

    // Did the authenticator actually accept largeBlob?
    //
    // Per WebAuthn L3, `largeBlob: { support: 'required' }` on create must either be honoured —
    // echoed back as `largeBlob: { supported: true }` — or make the ceremony fail. Some
    // providers do neither: they return success with EMPTY client extension results, leaving a
    // credential that can never carry a blob.
    //
    // Worth reporting, because the blob is the only channel for the biometric-proof JWE and a
    // credential genuinely lacking one can never verify — a failure that surfaces far from its
    // cause, as a later `credential_invalid` rather than anything at registration.
    //
    // But an empty result is NOT proof the blob is missing, and the warning must not claim it is.
    // Both mobile providers under-report here:
    //   - iOS, 2026-08-10: `clientExtensionResults: {}` at create, then the very same credential
    //     read 1085 bytes of blob at authentication and produced a JWE.
    //   - Android, 2026-08-11: same empty result; the ED (extension data) flag in authData is
    //     also clear, so nothing was negotiated at the WebAuthn layer either.
    // largeBlob is a *client* extension, so a clear ED flag is expected regardless and proves
    // nothing on its own — the only decisive test is whether a read at authentication returns
    // bytes. Hence "unconfirmed", not "failed".
    // Guarded: getClientExtensionResults is required by the spec, but this whole check exists
    // because providers do not always follow it — crashing a successful registration over a
    // missing diagnostic method would be a worse bug than the one being detected.
    const creationExtensions = (typeof credential.getClientExtensionResults === 'function'
      ? credential.getClientExtensionResults()
      : undefined) as { largeBlob?: { supported?: boolean } } | undefined;
    const largeBlobSupported = creationExtensions?.largeBlob?.supported === true;

    if (!largeBlobSupported) {
      const requested = options.largeBlobSupport || 'required';
      console.warn(
        `[FIDO2-JS] largeBlob support was requested as '${requested}' but the authenticator did ` +
          'not confirm it (client extension results: ' +
          `${JSON.stringify(creationExtensions || {})}).` +
          (requested === 'required'
            ? ' Per spec a required largeBlob should have failed the ceremony outright, so the ' +
              'provider is not reporting the extension correctly.'
            : '') +
          ' This does NOT by itself mean the credential has no blob: the iOS and Android ' +
          'providers have both returned empty extension results for credentials whose blob ' +
          'reads back fine at authentication. Treat it as unconfirmed, not failed — authenticate ' +
          'once and check whether the blob read returns bytes. If it comes back empty, then ' +
          'there is genuinely no biometric-proof JWE and verification cannot succeed.'
      );
    }

    return {
      credentialId: bufferToBase64(credential.rawId),
      attestationObject: bufferToBase64(response.attestationObject),
      clientDataJSON: bufferToBase64(response.clientDataJSON),
      largeBlobSupported,
      rawCredential: credential,
    };
  } catch (error: any) {
    // Handle specific WebAuthn error types
    if (error instanceof WebAuthnError) {
      throw error;
    }

    // Map DOMException errors to WebAuthnError.
    //
    // Every branch carries the browser's own name and message through to the WebAuthnError
    // message, not just into `details`. The requested option set here is unusually demanding
    // (residentKey 'required' + userVerification 'required' + attestation 'direct' + largeBlob
    // + credProtect), so a rejection is far more likely to be an authenticator refusing one of
    // those options than an actual user cancellation — and the two are only distinguishable by
    // the DOMException name.
    if (error instanceof DOMException) {
      if (error.name === 'NotAllowedError') {
        const detail = error.message ? ` Browser said: "${error.message}".` : '';

        throw new WebAuthnError(
          WebAuthnErrorCode.USER_CANCELLED,
          `Registration was not completed (NotAllowedError).${detail} Causes: the user ` +
            'dismissed the prompt or picker; the user declined the attestation ' +
            '("see the make and model of your security key") consent that attestation ' +
            "'direct' triggers on Windows; or the authenticator refused without a specific " +
            'reason.',
          { originalError: describeError(error) }
        );
      }
      if (error.name === 'TimeoutError' || error.name === 'AbortError') {
        throw new WebAuthnError(
          WebAuthnErrorCode.TIMEOUT,
          `Registration timed out (${error.name}).`,
          { originalError: describeError(error) }
        );
      }

      // These are the option-refusal names. They mean the ceremony reached the authenticator
      // and it declined a specific requested option — so the remedy is to relax that option,
      // never to retry the same request.
      const optionRefusalHints: Record<string, string> = {
        // Requested option unsupported: on Windows this is most often largeBlob with
        // support 'required' against an authenticator that has no large-blob storage, or a
        // pubKeyCredParams algorithm mismatch.
        NotSupportedError:
          "The authenticator does not support a requested option. Most likely largeBlob " +
          "support 'required' (try 'preferred'), or none of the requested algorithms " +
          '(ES256/RS256) are supported.',
        // A constraint could not be met: residentKey/UV are the usual culprits.
        ConstraintError:
          "The authenticator could not satisfy a constraint. Most likely residentKey " +
          "'required' (discoverable-credential storage full or unsupported) or " +
          "userVerification 'required' with no PIN/biometric enrolled.",
        // Do NOT read this as "a credential already exists" — for the UltraPass authenticator it
        // usually is not. the authenticator returns CTAP2_ERR_CREDENTIAL_EXCLUDED (0x19) from three
        // places, and the third deliberately overloads it:
        //
        //   1. a genuine excludeList match — we send excludeCredentials: [], so not this;
        //   2. its duplicate (rpId, username) product extension, keyed on the authenticator's own
        //      resident-key store rather than the browser's or Windows' cache;
        //   3. **biometric registration failed** — `bio->register_fn` returned non-zero. 0x19 is
        //      the only status that reliably halts Windows' ceremony retry loop, so the
        //      authenticator reuses it ("Overloads the 'excluded' meaning, but reliably halts the
        //      Windows retry loop").
        //
        // Timing separates them: 1 and 2 are checked before any side effects and fail in
        // milliseconds, while 3 fails only after a face capture has run — tens of seconds.
        InvalidStateError:
          'The authenticator refused with CREDENTIAL_EXCLUDED. For UltraPass this does NOT ' +
          'reliably mean a duplicate credential — it also returns this when FACE REGISTRATION ' +
          'FAILED, because it is the only error that stops Windows retrying. If this took more ' +
          'than a few seconds, a capture ran and the biometric step failed: check the ' +
          'authenticator service log for "Biometric register failed" and confirm the service\'s ' +
          'own apiKey/policyId and backend are right (its /status route reports them). Only if ' +
          'it failed instantly is it a real duplicate — then remove the existing passkey ' +
          '(Windows: Settings → Accounts → Passkeys), or change the username, since the ' +
          'authenticator also rejects a duplicate (rpId, username).',
        // Chrome on Android raises this when the request reached Android's Credential Manager
        // but no provider fulfilled it. Unlike its neighbours in this map it is NOT an option
        // refusal — the same request commonly succeeds on the next attempt, so retrying is the
        // right first move rather than relaxing an option.
        //
        // Observed on 2026-08-11: a registration failed here ~9s after the ceremony started, and
        // an identical retry 20s later completed normally. The Android flow fires
        // `privateid://init` and waits `androidInitDelay` before calling WebAuthn, so a cold
        // start of the UltraPass app can still be in progress when the ceremony begins.
        NotReadableError:
          "Android's Credential Manager could not complete the request, which usually means no " +
          'provider was ready rather than that anything was misconfigured. Retry first — this ' +
          'is frequently transient. If it repeats, the UltraPass app is likely still cold ' +
          'starting when the ceremony begins: raise `androidInitDelay` (default 1000ms). Also ' +
          'confirm UltraPass is enabled under Settings → Passwords & accounts → Passkeys, and ' +
          'that Google Play services is up to date.',
        // Windows/CTAP returned an error the browser could not map — commonly an
        // authenticator-side failure part-way through an otherwise successful enrollment.
        UnknownError:
          'The platform returned an unmapped error, typically a CTAP-level failure from the ' +
          'authenticator itself. Check the authenticator/device log for the underlying cause.',
        SecurityError:
          'The RP ID is not valid for this origin. The domain field must match the page ' +
          "origin's registrable suffix.",
      };

      const hint = optionRefusalHints[error.name];

      throw new WebAuthnError(
        WebAuthnErrorCode.CREATION_FAILED,
        `Credential creation failed with ${error.name}: "${error.message}".` +
          (hint ? ` ${hint}` : ''),
        { originalError: describeError(error) }
      );
    }

    // Generic error — not a DOMException, so this is a bug in our own post-processing
    // (buffer conversion, CBOR decode) rather than a WebAuthn refusal.
    throw new WebAuthnError(
      WebAuthnErrorCode.CREATION_FAILED,
      `Credential creation failed: ${error.message}`,
      { originalError: describeError(error) }
    );
  }
}

/**
 * Gets an existing WebAuthn credential (authentication).
 *
 * This function wraps the native `navigator.credentials.get()` API with:
 * - Automatic challenge generation
 * - Custom options encoding via PRF extension
 * - LargeBlob reading for JWE tokens
 * - Type-safe options and results
 * - Comprehensive error handling
 *
 * The function can authenticate with any registered credential or be restricted
 * to specific credentials via the `allowedCredentials` option.
 *
 * @param options - Options for credential retrieval
 * @returns A promise that resolves to the authentication result
 * @throws {WebAuthnError} If credential retrieval fails
 *
 * @example
 * ```typescript
 * try {
 *   const result = await getCredential({
 *     domain: 'example.com',
 *     customOptions: {
 *       checkLiveness: true,
 *       checkAge: true,
 *       ageThreshold: 18
 *     }
 *   });
 *
 *   console.log('Authenticated with credential:', result.credentialId);
 *
 *   if (result.jweToken) {
 *     // Send JWE token to server for verification
 *     console.log('JWE token:', result.jweToken);
 *   }
 * } catch (error) {
 *   if (error instanceof WebAuthnError) {
 *     console.error('Authentication failed:', error.code, error.message);
 *   }
 * }
 * ```
 *
 * @remarks
 * - Challenge: A random 32-byte value is generated if not provided
 * - Custom Options: Encoded into a 32-byte salt and passed via PRF extension
 * - LargeBlob: Automatically reads the blob and extracts JWE token
 * - PRF Results: Extracted from extension results if available
 * - Transports: Supports internal, hybrid, and USB authenticators
 */
export async function getCredential(
  options: GetCredentialOptions
): Promise<AuthenticationResult> {
  // Validate WebAuthn support
  if (!isWebAuthnSupported()) {
    throw new WebAuthnError(
      WebAuthnErrorCode.NOT_SUPPORTED,
      'WebAuthn is not supported in this browser'
    );
  }

  // Validate required parameters
  if (!options.domain || typeof options.domain !== 'string') {
    throw new WebAuthnError(
      WebAuthnErrorCode.INVALID_PARAMS,
      'domain is required and must be a string'
    );
  }

  warnIfRpIdTooLongForStore(options.domain);

  try {
    // Generate challenge if not provided
    const challenge = options.challenge || crypto.getRandomValues(new Uint8Array(32));

    // Build PublicKeyCredentialRequestOptions
    const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
      challenge: challenge as BufferSource,
      rpId: options.domain,
      timeout: options.timeout || 60000,
      userVerification: 'required', // Require biometric verification (matches reference POC)
      // Extensions will be added conditionally below
    };

    // Add allowed credentials if provided.
    //
    // The transports hint is a filter, not a preference: if it omits the transport the
    // authenticator actually uses, the credential cannot match and the browser rejects with
    // NotAllowedError — which looks exactly like a user cancellation. The PF adapter sends bare
    // `{ type, id }` entries for this reason; `transports: null` reproduces that.
    if (options.allowedCredentials && options.allowedCredentials.length > 0) {
      const transports =
        options.transports === undefined
          ? (['internal', 'hybrid', 'usb'] as AuthenticatorTransport[])
          : options.transports;

      publicKeyCredentialRequestOptions.allowCredentials = options.allowedCredentials.map(
        (credentialId) => ({
          id: base64ToBuffer(credentialId),
          type: 'public-key' as const,
          ...(transports && transports.length > 0 && { transports }),
        })
      );

      if (!transports || transports.length === 0) {
        console.log('allowCredentials sent without a transports hint (PF-adapter behaviour)');
      }
    }

    // ALWAYS request the largeBlob read — it carries the biometric-proof JWE, which is the
    // whole point of a face-verified assertion. Native requests it unconditionally
    // (matching the native iOS launcher, which reads largeBlob on assertion).
    //
    // This used to be gated on `options.customOptions`, so a plain authenticate() asked for
    // no blob at all and silently produced no JWE.
    publicKeyCredentialRequestOptions.extensions = {
      largeBlob: {
        read: true,
      },
    };

    // PRF is only meaningful when there are custom options to encode into the salt — and only
    // when the platform is willing to entertain the extension at all. The PF adapter, which is
    // the reference that works on Windows, requests PRF nowhere.
    if (options.customOptions && options.requestPrf === false) {
      console.log(
        'largeBlob read requested; PRF suppressed by config, so custom biometric options are ' +
          'NOT conveyed through this assertion'
      );
    } else if (options.customOptions) {
      const salt = encodeCustomOptionsToSalt(options.customOptions);
      (publicKeyCredentialRequestOptions.extensions as AuthenticationExtensionsClientInputs).prf = {
        eval: {
          first: salt as BufferSource,  // Pass Uint8Array directly (not salt.buffer)
        },
      };
      console.log('PRF + largeBlob extensions enabled with encoded custom options');
      console.log('Custom options:', options.customOptions);
      console.log('Encoded salt:', salt);
      console.log('Encoded salt (base64):', bufferToBase64(salt.buffer as ArrayBuffer));
    } else {
      console.log('largeBlob read requested (no custom options, so no PRF)');
    }

    console.log('Authentication options:', publicKeyCredentialRequestOptions);

    console.log('🔵 Calling navigator.credentials.get() - waiting for user interaction...');

    // Get the credential (with optional abort signal)
    // An explicit caller signal wins — it may be wired to a cancel button, and replacing it with
    // our own would silently disable that.
    const assertion = (await callWithEnforcedTimeout(
      (signal) =>
        navigator.credentials.get({
          publicKey: publicKeyCredentialRequestOptions,
          signal: options.signal || signal,
        }),
      options.timeout || 60000,
      options.enforceTimeoutWithAbort === true && !options.signal,
      'authentication'
    )) as PublicKeyCredential | null;

    console.log('🟢 navigator.credentials.get() completed! Got assertion:', assertion ? 'YES' : 'NULL');

    if (!assertion) {
      throw new WebAuthnError(
        WebAuthnErrorCode.RETRIEVAL_FAILED,
        'Credential retrieval returned null'
      );
    }

    // Extract response data
    const response = assertion.response as AuthenticatorAssertionResponse;

    // Extract extension results
    console.log('🔵 Extracting extension results...');
    const extensionResults = assertion.getClientExtensionResults();
    console.log('Extension results:', extensionResults);

    // Extract the biometric proof from the largeBlob extension
    let jweToken: string | null = null;
    let verifyPayload: string | null = null;
    let encryptedEmbedding: string | null = null;

    if (extensionResults.largeBlob && (extensionResults.largeBlob as any).blob) {
      const blobData = (extensionResults.largeBlob as any).blob as ArrayBuffer;
      const decoded = new TextDecoder().decode(blobData);
      console.log(`✓ Blob read successful (${blobData.byteLength} bytes)`);

      const parsed = parseLargeBlobPayload(decoded);
      jweToken = parsed.jweToken;
      verifyPayload = parsed.verifyPayload;
      encryptedEmbedding = parsed.encryptedEmbedding;
    } else if (options.fetchAssertionPayload) {
      // The Windows plugin-authenticator transport carries a ceremony as one CTAP command and the
      // platform does not forward largeBlob across it, so the blob is absent even though the JWE
      // was minted. Collect it from the authenticator's loopback control plane instead — see
      // core/loopback.ts. This is a delivery fallback, not a different proof.
      const fetched = await options.fetchAssertionPayload(
        bufferToBase64(response.clientDataJSON)
      );

      if (fetched) {
        const parsed = parseLargeBlobPayload(fetched);
        jweToken = parsed.jweToken;
        verifyPayload = parsed.verifyPayload;
        encryptedEmbedding = parsed.encryptedEmbedding;
        console.log('✓ Biometric proof recovered over the loopback /payload route');
      } else {
        console.warn(
          '[FIDO2-JS] No largeBlob in the assertion and the loopback /payload route returned ' +
            'nothing — no biometric proof available.\n' +
            'Note the assertion itself succeeded, so on the Windows plugin transport the ' +
            'authenticator service IS running (the provider forwards CTAP over that same port). ' +
            'The likeliest cause is therefore the page origin, not the service: browsers block a ' +
            'PUBLIC origin (an https trycloudflare/ngrok tunnel) from reaching loopback. Serve ' +
            'the page from http://localhost on the same machine and the fallback can connect. ' +
            'Other possibilities: PRIVID_CONFIG_WS_ALLOWED_ORIGINS excludes this origin, or the ' +
            'payload was already taken or expired (one-shot, 120s).'
        );
      }
    } else {
      // Not benign: no blob means no biometric proof, so face verification cannot be
      // checked. Callers that requested verification surface this as an error.
      console.warn(
        '[FIDO2-JS] No largeBlob data in assertion — no biometric proof available. ' +
          'If verification is expected, the credential may predate largeBlob support and ' +
          'need re-registration.'
      );
    }

    // Extract PRF results
    let prfResults: { first: string; second?: string } | null = null;
    if (extensionResults.prf && (extensionResults.prf as any).results) {
      const results = (extensionResults.prf as any).results;
      prfResults = {
        first: bufferToBase64(results.first),
      };
      if (results.second) {
        prfResults.second = bufferToBase64(results.second);
      }
      console.log('✓ PRF results (base64):', prfResults);
    } else {
      console.log('ℹ️ No PRF results available (this is OK)');
    }

    console.log('🟢 Building authentication result object...');

    return {
      credentialId: bufferToBase64(assertion.rawId),
      authenticatorData: bufferToBase64(response.authenticatorData),
      clientDataJSON: bufferToBase64(response.clientDataJSON),
      signature: bufferToBase64(response.signature),
      userHandle: response.userHandle ? bufferToBase64(response.userHandle) : null,
      jweToken,
      verifyPayload,
      encryptedEmbedding,
      prfResults,
      rawAssertion: assertion,
    };
  } catch (error: any) {
    // Handle specific WebAuthn error types
    if (error instanceof WebAuthnError) {
      throw error;
    }

    // Map DOMException errors to WebAuthnError
    if (error instanceof DOMException) {
      if (error.name === 'NotAllowedError') {
        // NotAllowedError is deliberately vague in the spec — browsers use it for a genuine
        // user cancellation AND for "no credential was usable", without distinguishing them.
        // Reporting it as a cancellation sends debugging down the wrong path, so name the
        // other causes too. The allowCredentials case is the common one on the deeplink
        // callback path: registration uses residentKey 'preferred', so a discovery-only
        // assertion can legitimately find nothing.
        // Include the browser's own message — Safari sometimes distinguishes a real
        // cancellation from "no credentials available", and the credential provider's
        // ASExtensionError description can surface here too (e.g. UltraPass returns
        // "Passkey needs to be re-registered" as credentialIdentityNotFound).
        const detail = error.message ? ` Browser said: "${error.message}".` : '';

        throw new WebAuthnError(
          WebAuthnErrorCode.USER_CANCELLED,
          `Authentication was not completed (NotAllowedError).${detail} Causes: the user ` +
            'dismissed the prompt; no usable credential was found; or the UltraPass credential ' +
            'provider refused. For the deeplink flow the provider requires a successful face ' +
            'verification result in shared storage from Phase 1 and the credential present in ' +
            'its resident-key store — check the UltraPass device log for "No verification ' +
            'result in shared storage" or "Credential ID not found in FIDO2 RK store".',
          { originalError: describeError(error) }
        );
      }
      if (error.name === 'TimeoutError' || error.name === 'AbortError') {
        throw new WebAuthnError(
          WebAuthnErrorCode.TIMEOUT,
          `Authentication timed out (${error.name}).`,
          { originalError: describeError(error) }
        );
      }

      // Name the DOMException rather than folding it into an anonymous failure.
      throw new WebAuthnError(
        WebAuthnErrorCode.RETRIEVAL_FAILED,
        `Credential retrieval failed with ${error.name}: "${error.message}".`,
        { originalError: describeError(error) }
      );
    }

    // Generic error
    throw new WebAuthnError(
      WebAuthnErrorCode.RETRIEVAL_FAILED,
      `Credential retrieval failed: ${error.message}`,
      { originalError: describeError(error) }
    );
  }
}
